import React, {useCallback, useEffect, useState} from "react";
import IconButton from "@mui/material/IconButton";
import {
    Card,
    CardActions,
    CardContent,
    CardHeader,
    FormControl,
    InputLabel,
    Select,
    TextField,
    MenuItem,
    Button, Grid, Alert, FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {Loader} from "../../../../../Components";
import {setRightDrawer} from "../../../../Common/App";
import {useDispatch, useSelector,} from "react-redux";
import {addUser} from "../../Redux/Action";
import {fetchRoles} from "../../../Roles";
import {checkEmailExists} from "../../../Users";

export const AddUser = () => {

    const dispatch = useDispatch();
    const roles = useSelector((store) => store.roles.list) || [];
    const [errors, setErrors] = useState({});
    const [message, setMessage] = useState("");
    const [status, setStatus] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const departments = useSelector((store) => store.app.config.departments);
    const [emailValidation, setEmailValidation] = useState({
        isValid: null, // null (not checked), true (valid), false (invalid)
        message: '',
    });

    const [data, setData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        mobile: '',
        event: '',
        designation: '',
        roles: [],
        role_type: 'EMPLOYEE'
    });

    const fetchRoleList = useCallback(async () => {
        await fetchRoles({dispatch, filters:{paginate:false}}).then(() => {
        });
    }, [dispatch]);

    useEffect(() => {
        fetchRoleList()
    }, [fetchRoleList]);

    const handleEmailBlur = async (e) => {
        const email = e.target.value;
        if (!email) {
            setEmailValidation({ isValid: null, message: '' });
            return;
        }

        // Basic email format validation (client-side only)
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setEmailValidation({ isValid: false, message: 'Please enter a valid email format.' });
            return;
        }

        // Check email existence only on blur
        setIsLoading(true);
        await checkEmailExists({dispatch, data:{email}}).then((res) => {
            if (res.success) {
                setEmailValidation({ isValid: true, message: 'This email is available.' });
            } else {
                setEmailValidation({ isValid: false, message: 'This email is already in use.' });
            }
            setIsLoading(false);
        });
    };

    const handleChange = (e) => {
        setData((prevData) => ({
            ...prevData,
            [e.target.name]: e.target.value,
        }));

        if (e.target.name === 'email') {
            // Clear validation message on change, but don't check existence yet
            setEmailValidation({ isValid: null, message: '' });
        }

        setErrors((prevErrors) => ({
            ...prevErrors,
            [e.target.name]: '', // Clear error for the field being changed
        }));
    };

    const handleSubmit = async () => {
        setIsLoading(true);
        await addUser({dispatch, data}).then((res) => {
            setIsLoading(false);
            if (res.success) {
                setData({
                    first_name: '',
                    last_name: '',
                    email: '',
                    mobile: '',
                    event: '',
                    designation: '',
                    department_id: '',
                    roles: [],
                    role_type: 'EMPLOYEE',
                });
                dispatch(setRightDrawer({component: null, open: false}));
            }
            setErrors(res.errors || {});
            setMessage(res.message);
            setStatus(res.status);
        });
    };

    return (
        <Card
            sx={{
                height: "100%",
                borderRadius: 0,
                position: "relative",
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                justifyContent: "flex-start",
                alignContent: "stretch",
            }}
        >
            <CardHeader
                action={
                    <IconButton aria-label="close" onClick={()=> dispatch(setRightDrawer({component:null, open:false}))}>
                        <CloseIcon />
                    </IconButton>
                }
                title={"New User"}
                avatar={
                    <Loader isLoading={isLoading} type="linear" />
                }
            />
            <CardContent sx={{ flex: 1, overflow: "auto" }}>
                <Grid container spacing={3}>
                    {message && (
                        <Grid item xs={12}>
                            <Alert severity={status}>{message}</Alert>
                        </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="First Name"
                            name="first_name"
                            variant="outlined"
                            size="large"
                            required
                            onChange={handleChange}
                            helperText={errors?.first_name}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            label="Last Name"
                            name="last_name"
                            variant="outlined"
                            size="large"
                            required
                            onChange={handleChange}
                            helperText={errors?.last_name}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Official Email"
                            name="email"
                            type="email"
                            variant="outlined"
                            size="large"
                            required
                            onChange={handleChange}
                            onBlur={handleEmailBlur}
                            error={emailValidation.isValid === false || errors?.email}
                            helperText={errors?.email || emailValidation.message}
                            FormHelperTextProps={{
                                sx: {
                                    color: emailValidation.isValid === true ? 'green' : emailValidation.isValid === false ? 'red' : 'inherit',
                                },
                            }}
                        />
                        <Loader type={'linear'} isLoading={isLoading} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Mobile"
                            name="mobile"
                            type="tel"
                            variant="outlined"
                            size="large"
                            required
                            onChange={handleChange}
                            helperText={errors?.mobile}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Designation"
                            name="designation"
                            variant="outlined"
                            size="large"
                            onChange={handleChange}
                            helperText={errors?.designation}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel id="add-user-form-label-department">Department</InputLabel>
                            <Select
                                label="Department"
                                name="department_id"
                                value={data.department_id}
                                onChange={handleChange}
                                variant="outlined"
                                required
                            >
                                {departments && departments.length > 0 && departments.map((department) => (
                                    <MenuItem key={department.id} value={department.id}>{department.department_name}</MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>{errors?.department_id}</FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel id="add-user-form-label-office">Role</InputLabel>
                            <Select
                                multiple
                                name="roles"
                                value={data.roles}
                                MenuProps={{
                                    style: {zIndex: 35001}
                                }}
                                fullWidth
                                label="Role"
                                variant="outlined"
                                onChange={(e) => handleChange(e)}
                            >
                                {roles && roles.length > 0 && roles.map((role) => (
                                    <MenuItem key={role.id} value={role.id}>{role.role_name}</MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>{errors?.roles}</FormHelperText>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel id="add-user-form-label-class">Role Type</InputLabel>
                            <Select
                                name="role_type"
                                value={data.role_type}
                                required
                                MenuProps={{
                                    style: { zIndex: 35001 },
                                }}
                                fullWidth
                                label="Role Type"
                                onChange={handleChange}
                                variant="outlined"
                            >
                                <MenuItem key={"ADMIN"} value={"ADMIN"}>{"Admin"}</MenuItem>
                                <MenuItem key={"EMPLOYEE"} value={"EMPLOYEE"}>{"Employee"}</MenuItem>
                                <MenuItem key={"MANAGER"} value={"MANAGER"}>{"Manager"}</MenuItem>
                            </Select>
                            <FormHelperText>{errors?.role_type}</FormHelperText>
                        </FormControl>
                    </Grid>
                </Grid>
            </CardContent>
            <CardActions className={"card-footer"} sx={{ p: 2, display: "flex", justifyContent: "space-between" }}>
                <Button variant="contained" color="primary" onClick={handleSubmit}>
                    Add User
                </Button>
                <Button variant="contained" color={"inherit"} onClick={()=> dispatch(setRightDrawer({component:null, open:false}))}>
                    Close
                </Button>
            </CardActions>
        </Card>
    );
};
